import { redirect, useLoaderData, type LoaderFunctionArgs } from "react-router";
import { useSetupPoll } from "../../components/setup-gate.jsx";
import { barLength, Loading, times } from "../../components/skeletons.jsx";
import { Deferred } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import type { WorkspaceApi, WorkspaceSummary } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { CheckIcon, LinkButton, PageHeader, Skeleton, SpinnerIcon } from "../../ui/index.js";

/**
 * Get started: the four things that make a workspace useful, each one line and one button. Every step
 * reads its state from the workspace itself, so there is nothing to tick off by hand. Once all four are
 * done the workspace opens on the Inbox and the sidebar drops this page. The agent step is the viewer's
 * own (a connection belongs to one person), so a newcomer to a set-up workspace still gets it.
 */

const STEPS = ["source", "decisions", "agent", "checks"] as const;
type Step = (typeof STEPS)[number];
type StepState = "done" | "running" | "todo";

export type Progress = Record<Step, StepState> & {
  /** Where the decisions to confirm wait: a read's candidates in Drafts, an agent's proposals in Reviews. */
  decisionsIn: "drafts" | "reviews";
};

export const isDone = (p: Progress) => STEPS.every((s) => p[s] === "done");

async function fallback<T>(p: Promise<T>, value: T): Promise<T> {
  try {
    return await p;
  } catch {
    return value;
  }
}

// The layout and the workspace's index both ask on the same navigation: one set of requests serves both.
const inFlight = new Map<string, Promise<Progress>>();

export function loadProgress(workspace: WorkspaceSummary, api: WorkspaceApi): Promise<Progress> {
  const key = `${workspace.id}:${workspace.setup ?? ""}`;
  let pending = inFlight.get(key);
  if (!pending) {
    pending = readProgress(workspace, api);
    void pending.finally(() => inFlight.delete(key)).catch(() => undefined);
    inFlight.set(key, pending);
  }
  return pending;
}

async function readProgress(workspace: WorkspaceSummary, api: WorkspaceApi): Promise<Progress> {
  const none = { items: [], nextCursor: null };
  const [accepted, proposed, candidates, agents, connectors, changes] = await Promise.all([
    fallback(api.listDecisions(undefined, { status: "accepted", limit: 1 }), none),
    fallback(api.listDecisions(undefined, { status: "proposed", limit: 1 }), none),
    fallback(api.candidates({ kind: "decision_candidate", status: "suggested" }), none),
    fallback(api.listAgentConnections(), { ...none, mcpUrl: "" }),
    fallback(api.listConnectors(), none),
    fallback(api.listChanges(), none),
  ]);
  const state = (done: boolean): StepState => (done ? "done" : "todo");
  return {
    source: workspace.setup === "running" ? "running" : state(!workspace.setup),
    decisions: state(accepted.items.some((d) => d.status === "accepted")),
    agent: state(agents.items.length > 0),
    checks: state(connectors.items.some((c) => c.provider === "github" && c.installation) || changes.items.length > 0),
    decisionsIn: candidates.items.length === 0 && proposed.items.some((d) => d.status === "proposed") ? "reviews" : "drafts",
  };
}

/** `/w/:slug`: Get started until it is done, then the Inbox. */
export async function workspaceIndexLoader({ context }: LoaderFunctionArgs) {
  const workspace = context.get(workspaceContext);
  const progress = await loadProgress(workspace, context.get(apiContext).workspace(workspace.id));
  return redirect(`/w/${workspace.slug}/${isDone(progress) ? "inbox" : "get-started"}`);
}

export function getStartedLoader({ context }: LoaderFunctionArgs) {
  const workspace = context.get(workspaceContext);
  const api = context.get(apiContext).workspace(workspace.id);
  return { workspace, api, progress: deferred(loadProgress(workspace, api)) };
}

export function GetStartedSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <PageHeader title={t("getStarted.title")} description={<Skeleton width={96} />} />
      <ol className="dt-steps">
        {times(STEPS.length).map((i) => (
          <li key={i} className="dt-steps__row">
            <Skeleton shape="circle" width={20} height={20} />
            <span className="dt-steps__text">
              <Skeleton width={barLength(i)} />
              <Skeleton width="60%" />
            </span>
          </li>
        ))}
      </ol>
    </Loading>
  );
}

export function GetStartedRoute() {
  const { workspace, api, progress } = useLoaderData<typeof getStartedLoader>();
  return (
    <Deferred data={progress} fallback={<GetStartedSkeleton />}>
      {(p) => <GetStarted progress={p} slug={workspace.slug} api={api} />}
    </Deferred>
  );
}

function GetStarted({ progress, slug, api }: { progress: Progress; slug: string; api: WorkspaceApi }) {
  const { t } = useI18n();
  // Sources being read: the step comes back by itself when the read is over.
  useSetupPoll(progress.source === "running", api);
  const base = `/w/${slug}`;
  const to: Record<Step, string> = {
    source: `${base}/setup`,
    decisions: `${base}/${progress.decisionsIn}`,
    agent: `${base}/settings/agents`,
    checks: `${base}/settings/connectors/github`,
  };
  const action: Record<Step, string> = {
    source: t("getStarted.source.action"),
    decisions: t(progress.decisionsIn === "reviews" ? "getStarted.decisions.action.reviews" : "getStarted.decisions.action.drafts"),
    agent: t("getStarted.agent.action"),
    checks: t("getStarted.checks.action"),
  };
  const next = STEPS.find((s) => progress[s] === "todo");
  const done = STEPS.filter((s) => progress[s] === "done").length;
  return (
    <>
      <PageHeader title={t("getStarted.title")} description={t("getStarted.progress", { done, total: STEPS.length })} />
      <ol className="dt-steps">
        {STEPS.map((step) => (
          <li key={step} className="dt-steps__row" data-state={progress[step]} data-testid={`step-${step}`}>
            <span className="dt-steps__mark" aria-hidden="true">
              {progress[step] === "done" ? <CheckIcon size={12} /> : progress[step] === "running" ? <SpinnerIcon size={14} /> : null}
            </span>
            <span className="dt-steps__text">
              <span className="dt-steps__title">{t(`getStarted.${step}.title`)}</span>
              <span className="dt-steps__body">{progress[step] === "running" ? t("getStarted.source.running") : t(`getStarted.${step}.body`)}</span>
            </span>
            {progress[step] === "todo" && (
              <LinkButton to={to[step]} variant={step === next ? "primary" : "secondary"} size="sm">
                {action[step]}
              </LinkButton>
            )}
          </li>
        ))}
      </ol>
    </>
  );
}
